import { Box } from "@mui/material";

import { format } from "date-fns";
import type {
  AvailableFilters,
  TransactionFilters,
} from "Components/Transactions/hooks/useTransactionFilterManager";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Shared.Filters;

type FilterItem = {
  key: keyof AvailableFilters;
  label: string;
  value?: string;
};

type Props = {
  filters: Partial<TransactionFilters>;
  availableFilters: AvailableFilters;
  isVisible?: boolean;
};

const formatDateRange = (
  dateFrom?: Date,
  dateTo?: Date,
): string | undefined => {
  if (!dateFrom && !dateTo) {
    return undefined;
  }

  const fromStr = dateFrom ? format(dateFrom, "d.M.yyyy") : "";
  const toStr = dateTo ? format(dateTo, "d.M.yyyy") : "";

  if (fromStr && toStr) {
    return `${fromStr} - ${toStr}`;
  }

  return fromStr || toStr;
};

export const TransactionFilterChips = ({
  filters: { timePeriod, dateFrom, dateTo, payPoint, employee, status },
  availableFilters,
  isVisible = false,
}: Props) => {
  const { t } = useResource();

  const getTimePeriodValue = (): string | undefined => {
    // Undefined/unset period ("Neomezeno") - don't show chip
    if (!timePeriod) {
      return undefined;
    }

    // Custom period - show actual dates
    if (timePeriod === "custom") {
      return (
        formatDateRange(dateFrom, dateTo) ??
        t(RESOURCES.TimePeriodLabels.custom)
      );
    }

    // All other periods (day, week, month) - show label
    return t(RESOURCES.TimePeriodLabels[timePeriod]);
  };

  const items: Array<FilterItem> = [
    {
      key: "timePeriod",
      label: t(RESOURCES.TimePeriod),
      value: getTimePeriodValue(),
    },
    {
      key: "payPoint",
      label: t(RESOURCES.PayPoint),
      value: payPoint?.label,
    },
    {
      key: "employee",
      label: t(RESOURCES.Employee),
      value: employee?.label,
    },
    {
      key: "status",
      label: t(RESOURCES.Status),
      value: status
        ? t(Resources.Transactions.TransactionStatus[status])
        : undefined,
    },
  ];

  // Only show filters that are available in this context and have a value
  const activeFilters = items.filter(
    ({ key, value }) => availableFilters[key] && !!value,
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: isVisible ? "1fr" : "0fr",
        transition: "grid-template-rows 300ms ease-in",
      }}
    >
      <Box sx={{ overflow: "hidden" }}>
        <Box component="dl" display="flex" flexWrap="wrap" gap={2} m={0}>
          {activeFilters.map(({ key, label, value }) => (
            <Box key={key} display="flex">
              <Box
                component="dt"
                sx={{ mr: 1, fontWeight: 600, color: "primary.main" }}
              >
                {label}:
              </Box>
              <Box component="dd" m={0} color="primary.main">
                {value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
